import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const TOKEN_KEY = "kk_access_token";

// Always include token if found
const setAuthHeader = (token) => {
  if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete axios.defaults.headers.common["Authorization"];
};

// --- Register User ---
export const registerUser = createAsyncThunk("auth/registerUser", async (data, thunkAPI) => {
  try {
    const res = await axios.post("/api/auth/register", data);
    const token = res.data.accessToken;
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      setAuthHeader(token);
    }
    return res.data.user;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || "Registration failed");
  }
});

// --- Login User ---
export const loginUser = createAsyncThunk("auth/loginUser", async (data, thunkAPI) => {
  try {
    const res = await axios.post("/api/auth/login", data);
    const token = res.data.accessToken;
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      setAuthHeader(token);
    }
    return res.data.user;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || "Login failed");
  }
});

// --- Validate Token (uses /api/me) ---
export const validateToken = createAsyncThunk("auth/validateToken", async (_, thunkAPI) => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) throw new Error("No token found");
    setAuthHeader(token);
    const res = await axios.get("/api/me");
    return res.data.user;
  } catch (err) {
    localStorage.removeItem(TOKEN_KEY);
    setAuthHeader(null);
    return thunkAPI.rejectWithValue("Session expired");
  }
});

// --- Logout User ---
export const logoutUser = createAsyncThunk("auth/logoutUser", async () => {
  localStorage.removeItem(TOKEN_KEY);
  setAuthHeader(null);
  return null;
});

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    isAuthenticated: false,
    loading: false,
    initialized: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(validateToken.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
        state.initialized = true;
      })
      .addCase(validateToken.rejected, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.initialized = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.initialized = true;
      });
  },
});

export const selectInitialized = (state) => state.auth.initialized;
export default authSlice.reducer;
